import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PrinterIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon,
  FireIcon,
  CakeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { 
  getAllKitchens,
  createKitchen, 
  updateKitchen, 
  deleteKitchen,
  getKitchenMenuItems,
  assignMenuItemsToKitchen,
  getPrinters,
  createPrinter,
  getVendorMenus
} from '../api/api';
import { useConfirm } from '../context/ConfirmContext';
import Modal from '../components/Modal';
import {
  PageHeroBand,
  SearchFilterBar,
  CardContainer,
  EmptyState,
  PrimaryButton,
  SecondaryButton,
  AppIconButton,
} from '../components/ui';
import type { FilterConfig } from '../components/ui';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  Paper,
  Avatar,
  Badge,
  CircularProgress,
  Button,
  Checkbox,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';

interface Kitchen {
  id: number;
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
  printerId?: number;
  printerName?: string;
  itemCount?: number;
  activeOrders?: number;
  createdAt: string;
  displayOrder?: number;
}

interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  isActive: boolean;
}

interface MenuItemAssignment {
  id: number;
  name: string;
  categoryName: string;
  isAssigned: boolean;
  currentKitchenId?: number;
  currentKitchenName?: string;
}

interface Menu {
  id: number;
  name: string;
  categories: Array<{
    id: number;
    name: string;
    items: MenuItemAssignment[];
  }>;
}

const KITCHEN_TYPES = [
  { value: 'MAIN', label: 'Main Kitchen', icon: <FireIcon className="h-5 w-5" />, color: 'var(--status-bad)' },
  { value: 'COLD', label: 'Cold Kitchen', icon: <BeakerIcon className="h-5 w-5" />, color: 'var(--text-link)' },
  { value: 'DESSERT', label: 'Dessert Station', icon: <CakeIcon className="h-5 w-5" />, color: 'var(--status-warn)' },
  { value: 'BAR', label: 'Bar', icon: <BeakerIcon className="h-5 w-5" />, color: 'var(--accent-alt)' },
  { value: 'SPECIALTY', label: 'Specialty', icon: <SparklesIcon className="h-5 w-5" />, color: 'var(--status-ok)' }
];

const KitchenManagement: React.FC = () => {
  const { theme } = useTheme();
  const confirm = useConfirm();
  
  // State management
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [isCreatePrinterOpen, setIsCreatePrinterOpen] = useState(false);
  const [editingKitchen, setEditingKitchen] = useState<Kitchen | null>(null);
  const [selectedKitchen, setSelectedKitchen] = useState<Kitchen | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    type: 'MAIN',
    description: '',
    isActive: true,
    printerId: ''
  });
  
  // Printer form data
  const [printerFormData, setPrinterFormData] = useState({
    name: '',
    ipAddress: '',
    port: '9100',
    type: 'THERMAL',
    isActive: true
  });
  
  // Menu item assignments
  const [assignedItems, setAssignedItems] = useState<number[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<number | null>(null);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  
  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Tab state for modal
  const [modalTab, setModalTab] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kitchensData, printersData, menusData] = await Promise.all([
        getAllKitchens(),
        getPrinters(),
        getVendorMenus()
      ]);
      
      // Sort kitchens by displayOrder (if exists) then by id for consistent ordering
      const sortedKitchens = (kitchensData || []).sort((a, b) => {
        // If both have displayOrder, sort by it
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }
        // If only one has displayOrder, prioritize it
        if (a.displayOrder !== undefined) return -1;
        if (b.displayOrder !== undefined) return 1;
        // Fallback to id
        return a.id - b.id;
      });
      
      setKitchens(sortedKitchens);
      setPrinters(printersData || []);
      
      // Ensure menus data has the expected structure
      const validMenus = (menusData || []).map(menu => ({
        ...menu,
        categories: menu.categories || []
      }));
      setMenus(validMenus);
    } catch (err: unknown) {
      console.error('Error fetching data:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    setSuccess('Data refreshed successfully');
  };

  const handleOpenModal = (kitchen?: Kitchen) => {
    if (kitchen) {
      setEditingKitchen(kitchen);
      setFormData({
        name: kitchen.name,
        type: kitchen.type,
        description: kitchen.description || '',
        isActive: kitchen.isActive,
        printerId: kitchen.printerId?.toString() || ''
      });
    } else {
      setEditingKitchen(null);
      setFormData({
        name: '',
        type: 'MAIN',
        description: '',
        isActive: true,
        printerId: ''
      });
    }
    setModalTab(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKitchen(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const kitchenData = {
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined,
        isActive: formData.isActive,
        printerId: formData.printerId ? parseInt(formData.printerId) : undefined
      };

      if (editingKitchen) {
        await updateKitchen(editingKitchen.id, kitchenData);
        setSuccess('Kitchen updated successfully');
      } else {
        await createKitchen(kitchenData);
        setSuccess('Kitchen created successfully');
      }

      handleCloseModal();
      fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save kitchen');
    }
  };

  const handleDelete = async (kitchen: Kitchen) => {
    const result = await confirm({
      title: 'Delete Kitchen',
      message: `Are you sure you want to delete ${kitchen.name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true
    });

    if (result) {
      try {
        await deleteKitchen(kitchen.id);
        setSuccess('Kitchen deleted successfully');
        fetchData();
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string } } };
        setError(error.response?.data?.error || 'Failed to delete kitchen');
      }
    }
  };

  const handleAssignMenuItems = async (kitchen: Kitchen) => {
    setSelectedKitchen(kitchen);
    
    try {
      // Fetch fresh menu data to ensure we have the latest
      const [menuItems, menusData] = await Promise.all([
        getKitchenMenuItems(kitchen.id),
        getVendorMenus()
      ]);
      
      setAssignedItems(menuItems.map((item: { id: number }) => item.id));
      
      // Ensure menus data has the expected structure
      const validMenus = (menusData || []).map(menu => ({
        ...menu,
        categories: menu.categories || []
      }));
      setMenus(validMenus);
      
      if (validMenus.length > 0) {
        setSelectedMenu(validMenus[0].id);
      }
      
      setIsAssignmentModalOpen(true);
    } catch (err: unknown) {
      setError('Failed to fetch menu item assignments');
    }
  };

  const handleSaveAssignments = async () => {
    if (!selectedKitchen) return;

    try {
      await assignMenuItemsToKitchen(selectedKitchen.id, assignedItems);
      setSuccess('Menu item assignments updated successfully');
      setIsAssignmentModalOpen(false);
      fetchData();
    } catch (err: unknown) {
      setError('Failed to update assignments');
    }
  };

  const handleCreatePrinter = async () => {
    try {
      const newPrinter = await createPrinter(printerFormData);
      setSuccess('Printer created successfully');
      setIsCreatePrinterOpen(false);
      
      // Update printers list
      const updatedPrinters = await getPrinters();
      setPrinters(updatedPrinters);
      
      // Set the new printer as selected
      setFormData({ ...formData, printerId: newPrinter.id.toString() });
      
      // Reset printer form
      setPrinterFormData({
        name: '',
        ipAddress: '',
        port: '9100',
        type: 'THERMAL',
        isActive: true
      });
    } catch (err: unknown) {
      setError('Failed to create printer');
    }
  };

  const getKitchenTypeInfo = (type: string) => {
    return KITCHEN_TYPES.find(t => t.value === type) || KITCHEN_TYPES[0];
  };

  // Filtering and pagination
  const filteredKitchens = kitchens.filter(kitchen => {
    const matchesSearch = kitchen.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         kitchen.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || kitchen.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const paginatedKitchens = filteredKitchens.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stats cards
  const stats = {
    total: kitchens.length,
    active: kitchens.filter(k => k.isActive).length,
    withPrinters: kitchens.filter(k => k.printerId).length,
    totalItems: kitchens.reduce((sum, k) => sum + (k.itemCount || 0), 0)
  };

  // Mirrors the Finance module's summary-card shell so both read as one system.
  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: '12px',
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
  };

  // Active stations is THE number for this page, so it lives in the hero band.
  // These three are supporting facts — plain counts, no status colour to earn.
  const summaryCards = [
    { label: 'Total Kitchens', value: stats.total },
    { label: 'With Printers', value: stats.withPrinters },
    { label: 'Total Items', value: stats.totalItems },
  ];

  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  const typeFilterConfig: FilterConfig[] = [
    {
      key: 'type',
      label: 'Type',
      options: [
        { value: 'all', label: 'All Types' },
        ...KITCHEN_TYPES.map(t => ({ value: t.value, label: t.label })),
      ],
      value: filterType,
      onChange: (value: string) => setFilterType(value),
      minWidth: 160,
    },
  ];

  const renderMobileKitchenCard = (kitchen: Kitchen) => {
    const typeInfo = getKitchenTypeInfo(kitchen.type);
    return (
      <Card 
        key={kitchen.id} 
        sx={{ 
          backgroundColor: theme.components?.card?.background, 
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
          mb: 2,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
                    {/* Header Row */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            width: '100%'
          }}>
            <Avatar sx={{ 
              backgroundColor: typeInfo.color + '20', 
              width: 40, 
              height: 40,
              flexShrink: 0 
            }}>
              {React.cloneElement(typeInfo.icon, { style: { color: typeInfo.color, width: 20, height: 20 } })}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  color: theme.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%'
                }}
              >
                {kitchen.name}
              </Typography>
              {kitchen.description && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.gray?.[600],
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    display: 'block'
                  }}
                >
                  {kitchen.description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Details Grid */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[600], textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Type
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {React.cloneElement(typeInfo.icon, { style: { color: typeInfo.color, width: 16, height: 16 } })}
                  <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500 }}>
                    {typeInfo.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[600], textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={kitchen.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    icon={kitchen.isActive ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
                    sx={{
                      backgroundColor: kitchen.isActive ? `${theme.success}20` : `${theme.gray?.[500]}20`,
                      color: kitchen.isActive ? theme.success : theme.gray?.[500],
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[600], textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Printer
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <PrinterIcon className="h-4 w-4" style={{ color: theme.gray?.[600] }} />
                  <Typography variant="body2" sx={{ color: theme.text }}>
                    {kitchen.printerName || 'No printer'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {/* Right column - Active orders */}
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.gray?.[600], textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>Active Orders</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {kitchen.activeOrders && kitchen.activeOrders > 0 ? (
                    <Badge badgeContent={kitchen.activeOrders} color="error">
                      <ClockIcon className="h-4 w-4" style={{ color: theme.warning }} />
                    </Badge>
                  ) : (
                    <ClockIcon className="h-4 w-4" style={{ color: theme.gray?.[600] }} />
                  )}
                  <Typography variant="body2" sx={{ color: theme.text }}>
                    {kitchen.activeOrders || 0}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          {/* Action Buttons Row */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 1, 
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.components?.card?.border}`
          }}>
            <AppIconButton variant="secondary" tooltip="Edit" icon={<PencilSquareIcon style={{ width: 18, height: 18 }} />} size="small" onClick={() => handleOpenModal(kitchen)} />
            <AppIconButton variant="delete" tooltip="Delete" icon={<TrashIcon style={{ width: 18, height: 18 }} />} size="small" onClick={() => handleDelete(kitchen)} disabled={Boolean(kitchen.activeOrders && kitchen.activeOrders > 0)} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: theme.background, minHeight: '100vh' }}>
      {/* Header — confident-shell hero band, same as the Finance module */}
      <PageHeroBand
        eyebrow="Settings · Kitchens"
        title="Kitchen Management"
        heroLabel="Active stations"
        heroValue={String(stats.active)}
        actions={<Box sx={{ display: 'flex', gap: 1, flexShrink: 0,
          '& .MuiButton-root': { backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)', color: 'var(--band-text)', borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)', borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)' } } }}>
          <SecondaryButton
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={<ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />}
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Refresh
          </SecondaryButton>
          <SecondaryButton
            onClick={() => handleOpenModal()}
            startIcon={<PlusIcon className="h-5 w-5" />}
            data-testid="add-kitchen-button"
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Add Kitchen
          </SecondaryButton>
        </Box>}
      />

      {/* Summary cards — white surface, coloured number, honest zero state */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {summaryCards.map((card) => (
          <Grid item xs={4} sm={4} md={4} key={card.label}>
            <Paper elevation={0} sx={cardSx}>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--text-tertiary)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  letterSpacing: '0.05em',
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  // Same rule as Finance: nothing to report reads quiet, not bold.
                  fontWeight: card.value === 0 ? 600 : 700,
                  color: card.value === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  fontSize: { xs: '1.35rem', sm: '1.75rem' },
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.15,
                  mt: 0.25,
                }}
              >
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Table Section */}
      <CardContainer disableHover>
        {/* Search & Filter Bar */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
          <SearchFilterBar
            searchPlaceholder="Search kitchens..."
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            filters={typeFilterConfig}
          />
        </Box>

        {/* Loading Bar */}
        {(loading || refreshing) && <LinearProgress />}

        {/* Conditional Rendering: Mobile Cards vs Desktop Table */}
        {isMobile ? (
          /* Mobile Card Layout */
          <Box sx={{ 
            p: 3, 
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'block !important'
          }}>
            {loading && !refreshing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : paginatedKitchens.length === 0 ? (
              <EmptyState
                icon={<BuildingStorefrontIcon className="h-8 w-8" />}
                title="No kitchens found"
                data-testid="kitchen-empty-state-mobile"
              />
            ) : (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {paginatedKitchens.map((kitchen) => renderMobileKitchenCard(kitchen))}
              </Box>
            )}
          </Box>
        ) : (
          /* Desktop Table Layout */
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: '34%' }}>Kitchen</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: 180 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: 140 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: 220 }}>Printer</TableCell>
                  {/* Removed Menu Items column per request (UI only) */}
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: 140 }}>Active Orders</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.text, width: 140 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !refreshing ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : paginatedKitchens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 0 }}>
                      <EmptyState
                        icon={<BuildingStorefrontIcon className="h-8 w-8" />}
                        title="No kitchens found"
                        data-testid="kitchen-empty-state-desktop"
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedKitchens.map((kitchen) => {
                    const typeInfo = getKitchenTypeInfo(kitchen.type);
                    return (
                      <TableRow key={kitchen.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ backgroundColor: typeInfo.color + '20' }}>
                              {React.cloneElement(typeInfo.icon, { style: { color: typeInfo.color } })}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text }}>
                                {kitchen.name}
                              </Typography>
                              {kitchen.description && (
                                <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                                  {kitchen.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={typeInfo.label} 
                            size="small"
                            icon={React.cloneElement(typeInfo.icon, { className: 'h-4 w-4' })}
                            sx={{ 
                              backgroundColor: typeInfo.color + '20', 
                              color: typeInfo.color,
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: typeInfo.color }
                            }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            label={kitchen.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: kitchen.isActive ? `${theme.success}20` : `${theme.danger}20`,
                              color: kitchen.isActive ? theme.success : theme.danger,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          {kitchen.printerName ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PrinterIcon className="h-4 w-4" style={{ color: theme.gray?.[600] }} />
                              <Typography variant="body2">{kitchen.printerName}</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: theme.gray?.[500] }}>
                              No printer
                            </Typography>
                          )}
                        </TableCell>
                        
                        {/* Menu Items cell removed (UI only) */}
                        
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ClockIcon className="h-4 w-4" style={{ color: theme.warning }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.warning }}>
                              {kitchen.activeOrders || 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            
                            <AppIconButton variant="secondary" tooltip="Edit" icon={<PencilSquareIcon className="h-5 w-5" />} size="small" onClick={() => handleOpenModal(kitchen)} />
                            <AppIconButton variant="delete" tooltip="Delete" icon={<TrashIcon className="h-5 w-5" />} size="small" onClick={() => handleDelete(kitchen)} disabled={Boolean(kitchen.activeOrders && kitchen.activeOrders > 0)} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredKitchens.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </CardContainer>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingKitchen ? 'Edit Kitchen' : 'Add New Kitchen'}
        maxWidth="md"
      >
        <Box sx={{ mt: 2 }}>
          <Tabs 
            value={modalTab} 
            onChange={(_, v) => setModalTab(v)} 
            sx={{ 
              mb: 3,
              '& .MuiTabs-flexContainer': {
                gap: 2
              },
              '& .MuiTab-root': {
                minWidth: { xs: 80, sm: 120 },
                px: { xs: 1.5, sm: 3 },
                py: 1.5,
                mx: { xs: 0.5, sm: 1 },
                borderRadius: 1.5,
                // Tokens, not theme.primary — a stale legacy preset must not
                // repaint the selected tab (see action-colour-contract test).
                border: `color-mix(in srgb, var(--action) 27%, transparent) !important 1px solid` as any,
                backgroundColor: `${'var(--bg-surface)'} !important`,
                color: `${theme.text} !important`,
                fontWeight: 500,
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: `color-mix(in srgb, var(--action) 13%, transparent) !important`,
                  borderColor: `var(--action) !important`,
                  color: `var(--action) !important`,
                  boxShadow: 'none'
                },
                '&:hover': {
                  backgroundColor: `color-mix(in srgb, var(--action) 7%, transparent) !important`
                }
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab label="Basic Information" />
            <Tab label="Configuration" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {modalTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Kitchen Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    fullWidth
                    placeholder="e.g., Main Kitchen, Cold Station"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Kitchen Type</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      label="Kitchen Type"
                    >
                      {KITCHEN_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {React.cloneElement(type.icon, { style: { color: type.color } })}
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="Active Status"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Brief description of this kitchen station..."
                  />
                </Grid>
              </Grid>
            )}

            {modalTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Associated Printer</InputLabel>
                    <Select
                      value={formData.printerId}
                      onChange={(e) => {
                        if (e.target.value === 'create-new') {
                          setIsCreatePrinterOpen(true);
                        } else {
                          setFormData({ ...formData, printerId: e.target.value });
                        }
                      }}
                      label="Associated Printer"
                    >
                      <MenuItem value="">
                        <em>No Printer</em>
                      </MenuItem>
                      {printers.filter(p => p.isActive).map(printer => (
                        <MenuItem key={printer.id} value={printer.id.toString()}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PrinterIcon className="h-4 w-4" />
                            {printer.name} ({printer.ipAddress})
                          </Box>
                        </MenuItem>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <MenuItem value="create-new">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.primary }}>
                          <PlusIcon className="h-4 w-4" />
                          Create New Printer
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    Assign a printer to automatically print KOT (Kitchen Order Tickets) for this kitchen
                  </Alert>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <SecondaryButton onClick={handleCloseModal}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit">
                {editingKitchen ? 'Update' : 'Create'} Kitchen
              </PrimaryButton>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Menu Item Assignment Modal */}
      <Modal
        isOpen={isAssignmentModalOpen}
        onClose={() => {
          setIsAssignmentModalOpen(false);
          setItemSearchTerm('');
        }}
        title={`Assign Menu Items to ${selectedKitchen?.name}`}
        maxWidth="lg"
      >
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Select which menu items should be prepared in this kitchen. Orders containing these items will be routed here.
          </Alert>

          {menus.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Select Menu</InputLabel>
                  <Select
                    value={selectedMenu || ''}
                    onChange={(e) => setSelectedMenu(Number(e.target.value))}
                    label="Select Menu"
                  >
                    {menus.map(menu => (
                      <MenuItem key={menu.id} value={menu.id}>
                        {menu.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  placeholder="Search items..."
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlassIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Box sx={{ maxHeight: 400, overflow: 'auto', border: `1px solid ${theme.components?.card?.border}`, borderRadius: 1 }}>
                {menus.find(m => m.id === selectedMenu)?.categories?.map(category => {
                  const filteredItems = category.items?.filter(item => 
                    item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
                  ) || [];
                  
                  if (filteredItems.length === 0) return null;
                  
                  return (
                    <Box key={category.id}>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: theme.components?.card?.hover,
                        borderBottom: `1px solid ${theme.components?.card?.border}`,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                          {category.name}
                        </Typography>
                      </Box>
                      {filteredItems.map(item => (
                      <Box
                        key={item.id}
                        sx={{
                          px: 2,
                          py: 1,
                          borderBottom: `1px solid ${theme.components?.card?.border}`,
                          '&:hover': { backgroundColor: theme.components?.card?.hover }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={assignedItems.includes(item.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAssignedItems([...assignedItems, item.id]);
                                } else {
                                  setAssignedItems(assignedItems.filter(id => id !== item.id));
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ color: theme.text }}>
                                {item.name}
                              </Typography>
                              {item.currentKitchenName && item.currentKitchenId !== selectedKitchen?.id && (
                                <Chip
                                  label={item.currentKitchenName}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.75rem',
                                    backgroundColor: theme.gray?.[200],
                                    color: theme.gray?.[700]
                                  }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                  );
                })}
              </Box>

              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                    {assignedItems.length} items selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      const selectedMenuData = menus.find(m => m.id === selectedMenu);
                      if (selectedMenuData) {
                        const allVisibleItems = selectedMenuData.categories.flatMap(category => 
                          (category.items?.filter(item => 
                            item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
                          ) || []).map(item => item.id)
                        );
                        setAssignedItems(Array.from(new Set([...assignedItems, ...allVisibleItems])));
                      }
                    }}
                  >
                    Select Visible
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    onClick={() => setAssignedItems([])}
                    disabled={assignedItems.length === 0}
                  >
                    Clear All
                  </Button>
                  <SecondaryButton onClick={() => {
                    setIsAssignmentModalOpen(false);
                    setItemSearchTerm('');
                  }}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton onClick={handleSaveAssignments}>
                    Save Assignments
                  </PrimaryButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Create Printer Modal */}
      <Modal
        isOpen={isCreatePrinterOpen}
        onClose={() => setIsCreatePrinterOpen(false)}
        title="Create New Printer"
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Printer Name"
                value={printerFormData.name}
                onChange={(e) => setPrinterFormData({ ...printerFormData, name: e.target.value })}
                fullWidth
                required
                placeholder="e.g., Kitchen Printer 1"
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label="IP Address"
                value={printerFormData.ipAddress}
                onChange={(e) => setPrinterFormData({ ...printerFormData, ipAddress: e.target.value })}
                fullWidth
                required
                placeholder="192.168.1.100"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Port"
                value={printerFormData.port}
                onChange={(e) => setPrinterFormData({ ...printerFormData, port: e.target.value })}
                fullWidth
                required
                placeholder="9100"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Printer Type</InputLabel>
                <Select
                  value={printerFormData.type}
                  onChange={(e) => setPrinterFormData({ ...printerFormData, type: e.target.value })}
                  label="Printer Type"
                >
                  <MenuItem value="THERMAL">Thermal Printer (80mm/58mm)</MenuItem>
                  <MenuItem value="IMPACT">Impact Printer (Dot Matrix)</MenuItem>
                  <MenuItem value="LASER">Laser Printer</MenuItem>
                  <MenuItem value="NETWORK">Network Printer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={printerFormData.isActive}
                    onChange={(e) => setPrinterFormData({ ...printerFormData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => setIsCreatePrinterOpen(false)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleCreatePrinter}>
                  Create Printer
                </PrimaryButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  );
};

export default KitchenManagement;